import FormatCurrency from 'components/Common/FormatCurrency'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray } from 'lib/array/arrayUtils'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import { useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import productBadgeMessages from 'i18n/productBadgeMessages'

interface Props {
  price?: number
  propertyFees?: number
  flightPrice?: number
  duration: number
  adultsNumber: number
  withFlights?: boolean
  displayPricingAsPerPerson: boolean
  rooms: Array<App.RoomOccupants>
}

function CalendarPriceBlurb(props: Props) {
  const { price, propertyFees, flightPrice, duration, adultsNumber, withFlights, displayPricingAsPerPerson, rooms = EmptyArray } = props
  const intl = useIntl()

  const hotelPrice = price ?? 0
  const hotelPropertyFees = propertyFees ?? 0

  const {
    total,
    saleUnit,
  } = getCalendarDisplayTotalPrice({
    price: hotelPrice + hotelPropertyFees,
    adultsNumber,
    nights: duration,
    roomsNumber: rooms.length,
    withFlights: !!withFlights,
    flightPrice: flightPrice ?? 0,
    displayPricingPer: displayPricingAs(displayPricingAsPerPerson),
  })

  const showFlightsAfterNights = !!withFlights && !!flightPrice
  const showFlightsInSuffix = !!withFlights && !flightPrice && !displayPricingAsPerPerson
  const isMultiRoom = rooms.length > 1

  const blurb = useMemo(() => {
    const boldValue = (chunks: React.ReactNode) => (
      <BodyText key="bold" as="span" variant="medium" colour="normal-contrast" weight="bold">{chunks}</BodyText>
    )
    const captionValue = (chunks: React.ReactNode) => (
      <Caption key="caption" variant="medium" as="span">{chunks}</Caption>
    )
    const priceValue = <FormatCurrency key="price" value={total} />
    const roomCountLabel = intl.formatMessage(
      {
        id: 'MgOIGioysUztn2IRN4EC',
        defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}',
      },
      { roomCount: rooms.length },
    )
    const nightCountLabel = intl.formatMessage(
      {
        id: 'zhpWqc4HYqUF1GbTSG7I',
        defaultMessage: '{nightCount, plural, one {# night} other {# nights}}',
      },
      { nightCount: duration },
    )

    if (isMultiRoom) {
      if (displayPricingAsPerPerson && showFlightsAfterNights) {
        return intl.formatMessage(
          {
            id: 'X63dhHLcVBRiq9MkeoLD',
            defaultMessage: '{roomCountLabel}, {nightCountLabel} + flights from <bold>{price}<caption>{saleUnit}</caption></bold>',
          },
          { roomCountLabel, nightCountLabel, price: priceValue, saleUnit, bold: boldValue, caption: captionValue },
        )
      }

      if (displayPricingAsPerPerson) {
        return intl.formatMessage(
          {
            id: 'JUbsQJYgmilRW63U6WL5',
            defaultMessage: '{roomCountLabel}, {nightCountLabel} from <bold>{price}<caption>{saleUnit}</caption></bold>',
          },
          { roomCountLabel, nightCountLabel, price: priceValue, saleUnit, bold: boldValue, caption: captionValue },
        )
      }

      if (showFlightsAfterNights) {
        return intl.formatMessage(
          {
            id: 'sg1bhZjYO4vqwrg_CNuR',
            defaultMessage: '{roomCountLabel}, {nightCountLabel} + flights from <bold>{price}</bold> {saleUnit}.',
          },
          { roomCountLabel, nightCountLabel, price: priceValue, saleUnit, bold: boldValue },
        )
      }

      if (showFlightsInSuffix) {
        return intl.formatMessage(
          {
            id: 'IupvdvN_V29Vy-GnXv9Z',
            defaultMessage: '{roomCountLabel}, {nightCountLabel} from <bold>{price}</bold> {saleUnit} + flights.',
          },
          { roomCountLabel, nightCountLabel, price: priceValue, saleUnit, bold: boldValue },
        )
      }

      return intl.formatMessage(
        {
          id: 'PdL67hCir0KiHY5nHmhv',
          defaultMessage: '{roomCountLabel}, {nightCountLabel} from <bold>{price}</bold> {saleUnit}.',
        },
        { roomCountLabel, nightCountLabel, price: priceValue, saleUnit, bold: boldValue },
      )
    }

    if (displayPricingAsPerPerson && showFlightsAfterNights) {
      return intl.formatMessage(
        {
          id: 'VFF2ku3KWRxASOmZqZGL',
          defaultMessage: '{nightCountLabel} + flights from <bold>{price}<caption>{saleUnit}</caption></bold>',
        },
        { nightCountLabel, price: priceValue, saleUnit, bold: boldValue, caption: captionValue },
      )
    }

    if (displayPricingAsPerPerson) {
      return intl.formatMessage(
        {
          id: 'sNpIVAeXDfFTx8b-cAPL',
          defaultMessage: '{nightCountLabel} from <bold>{price}<caption>{saleUnit}</caption></bold>',
        },
        { nightCountLabel, price: priceValue, saleUnit, bold: boldValue, caption: captionValue },
      )
    }

    if (showFlightsAfterNights) {
      return intl.formatMessage(
        {
          id: 'hpEk23rEWWSa2yBlyu3_',
          defaultMessage: '{nightCountLabel} + flights from <bold>{price}</bold> {saleUnit}.',
        },
        { nightCountLabel, price: priceValue, saleUnit, bold: boldValue },
      )
    }

    if (showFlightsInSuffix) {
      return intl.formatMessage(
        {
          id: 'fr5EidKZHkLGOvu8Ay75',
          defaultMessage: '{nightCountLabel} from <bold>{price}</bold> {saleUnit} + flights.',
        },
        { nightCountLabel, price: priceValue, saleUnit, bold: boldValue },
      )
    }

    return intl.formatMessage(
      {
        id: 'w6wQGE547cNT6PltzulB',
        defaultMessage: '{nightCountLabel} from <bold>{price}</bold> {saleUnit}.',
      },
      { nightCountLabel, price: priceValue, saleUnit, bold: boldValue },
    )
  }, [intl, total, displayPricingAsPerPerson, showFlightsAfterNights, showFlightsInSuffix, isMultiRoom, rooms.length, duration, saleUnit])

  return <Group
    direction="vertical"
    gap={4}
    tabletDirection="horizontal"
    tabletHorizontalAlign="space-between"
  >
    <div>
      <BodyText variant="medium" colour="light-foreground-alt">
        {blurb}
      </BodyText>
      <BodyText variant="medium" colour="normal-contrast">
        {intl.formatMessage({
          id: 'k__r_VMoM7NwLhPJJOMc',
          defaultMessage: '* Price is indicative. Select your dates to view best available rates',
        })}
      </BodyText>
    </div>
    <BodyTextBlock variant="medium" startIcon={<SolidFireIcon colour="urgency" />}>
      {intl.formatMessage(productBadgeMessages.limitedTimeSpecial)}
    </BodyTextBlock>
  </Group>
}

export default withOfferPageState((state) => ({
  duration: state.duration,
  withFlights: state.flights,
  rooms: state.rooms,
}), CalendarPriceBlurb)
